import { useState, useEffect } from 'react';
import { FilterItemDto, QueryAndSortDto } from 'models/dtoTypes';
import { IFilterField } from 'models/types';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import FilterPanel from 'components/FilterPanel';
import { ITableRowAction, SingleItem } from './ResourceTable';

const PREFIX = 'ResourceFullListTable';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: 'black',
    fontWeight: 'bold',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    // borderRadius: 5,
    borderTop: '1px solid',
    borderBottom: '1px solid',
    '&:first-of-type': {
      borderLeft: '1px solid',
      borderTopLeftRadius: 5,
      borderBottomLeftRadius: 5
    },
    '&:last-child': {
      borderRight: '1px solid',
      borderTopRightRadius: 5,
      borderBottomRightRadius: 5,
      // transform: 'scale(1.05)',
    }
  },
}));

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
  pagination: `${PREFIX}-pagination`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.pagination}`]: {
    paddingTop: 10,
    paddingBottom: 10,
  }
}));

export interface IColumn<TAttributes> {
  label: string;
  dataIndex?: string;
  sortIndex?: string;
  actions?: ITableRowAction<TAttributes>[];
  render?: (model: TAttributes) => JSX.Element | string;
}


interface IProps<TAttributes> {
  filterSchema?: IFilterField[];
  columns: IColumn<TAttributes>[];
  onFetch: (options: QueryAndSortDto) => Promise<TAttributes[]>;
  defaultQuery?: FilterItemDto[];
  defaultSort?: { sortColumn: string, isDesc: boolean };
  onFiltersChange?: (filters: IFilters) => void | undefined;
}

interface IFilters {
  [key: string]: string;
}

export default function ResourceFullListTable<TAttributes>(props: IProps<TAttributes>) {
  const {
    columns,
    onFetch,
    defaultQuery = [],
    filterSchema,
    defaultSort,
    onFiltersChange = undefined,
  } = props;
  const [filters, setFilters] = useState<IFilters>({});
  const [filtered, setFiltered] = useState(false);
  const [sort, setSort] = useState<{ sortColumn: string, isDesc: boolean } | undefined>(defaultSort);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TAttributes[]>();

  const loadResources = async (reset = false) => {
    setLoading(true);
    const filterItems = reset ? [] : Object.keys(filters).map(k => ({ column: k, value: filters[k] }));
    const options = {
      filters: [
        ...defaultQuery,
        ...filterItems
      ],
      ...(sort || {}),
    };
    const data = await onFetch(options);
    setResult(data);
    setLoading(false);
  };

  useEffect(() => {
    void loadResources();
  }, [sort]);

  const rows = result || [];

  const handleChangeFilter = (value: string, name: string) => {
    setFilters({
      ...filters,
      [name]: value,
    });
    typeof onFiltersChange === 'function' && onFiltersChange({
      ...filters,
      [name]: value,
    });
  };

  const handleFilter = async () => {
    setLoading(true);

    const hasFilters = Object.values(filters).find(v => v !== undefined && v !== null);
    if (hasFilters) {
      await loadResources();
      setFiltered(true);
    }
    setLoading(false);
  };

  const handleReset = async () => {
    setLoading(true);
    if (filtered) {
      setFilters({});
      setFiltered(false);
      await loadResources(true);
    }
    setLoading(false);
  };

  const handleSort = (sortColumn: string) => {
    const previousSortIndex = sort?.sortColumn;
    if (previousSortIndex === sortColumn) {
      setSort({
        sortColumn,
        isDesc: !sort?.isDesc,
      });
    } else {
      setSort({
        sortColumn,
        isDesc: false,
      });
    }
  };


  const renderLoadingRows = () => {
    return (
      <>
        <LinearProgress />
      </>
    );
  };

  const renderTableHead = () => {
    const { sortColumn, isDesc } = sort || {};
    return (
      <TableHead>
        <TableRow>
          {columns.map((c, index) => {
            const { label, sortIndex } = c;
            if (sortIndex) {
              return (
                <StyledTableCell key={index}>
                  <TableSortLabel
                    active={sortColumn === sortIndex}
                    direction={isDesc ? 'desc' : 'asc'}
                    onClick={() => handleSort(sortIndex)}
                  >
                    {label}
                  </TableSortLabel>
                </StyledTableCell>
              );
            }
            return (
              <StyledTableCell key={index}>
                {label}
              </StyledTableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  };

  return (
    <Root>
      {filterSchema && (
        <FilterPanel
          fields={filterSchema}
          filtered={filtered}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          onSearch={handleFilter}
          onReset={handleReset}
        />
      )}
      <TableContainer component={Paper}>
        {!result && loading && renderLoadingRows()}
        <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
          {renderTableHead()}
          <TableBody>
            {rows.map((row) => {
              const rowObj = new Object(row);
              const key = Object.values(rowObj).reduce((acc, item) => String(acc) + String(item), '');
              return <SingleItem key={key} row={row} columns={columns} />;
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Root>
  );
}
